import { theme } from "@shared/utils/themeColors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { SubscriptionService } from "../services/subscription.service";

/**
 * Page for confirming subscription upgrade from monthly to yearly plan
 */
const SubscriptionUpgradeToYearlyConfirmPage: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Upgrade monthly to yearly mutation
  const { mutate: upgradeMonthlyToYearly } = useMutation({
    mutationFn: (subId: string) =>
      SubscriptionService.upgradeMonthlyToYearly(subId),
    onSuccess: (data) => {
      if (data.success) {
        // Check if we have a payment session URL
        if (data.sessionUrl) {
          // Redirect to Stripe checkout
          window.location.href = data.sessionUrl;
        } else {
          // Legacy behavior - no payment required
          toast.success(data.message || "Successfully scheduled upgrade to yearly plan");
          queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
          navigate("/settings/subscription-change/upgrade-yearly/success");
        }
      } else {
        toast.error(data.message || "Failed to upgrade to yearly plan");
        setIsProcessing(false);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Error upgrading to yearly plan");
      setIsProcessing(false);
    },
  });

  const handleConfirmUpgrade = () => {
    if (!subscriptionId) {
      toast.error("Subscription ID is missing");
      return;
    }

    setIsProcessing(true);
    upgradeMonthlyToYearly(subscriptionId);
  };

  const handleGoBack = () => {
    navigate("/settings/plans");
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1
            className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}
          >
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              <div className="p-6">
                <div className="max-w-md mx-auto">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <svg
                      className="w-8 h-8 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>

                  <h2
                    className={`text-2xl font-bold text-center ${theme.text.primary} mb-4`}
                  >
                    Switch to Yearly Billing
                  </h2>

                  <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
                    Are you sure you want to switch from monthly to yearly
                    billing? This will require an immediate payment for the yearly plan.
                    Yearly billing offers significant savings compared to paying monthly
                    over the course of a year.
                  </p>

                  <div className="space-y-4">
                    <button
                      onClick={handleConfirmUpgrade}
                      disabled={isProcessing}
                      className={`w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors ${
                        isProcessing ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isProcessing
                        ? "Processing..."
                        : "Yes, Proceed to Payment"}
                    </button>

                    <button
                      onClick={handleGoBack}
                      disabled={isProcessing}
                      className={`w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors ${
                        isProcessing ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      No, Go Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionUpgradeToYearlyConfirmPage;
